import { apiTableMessages } from 'components/ApiTable/apiTableMessages';
import { apiTablePageMessages } from 'components/ApiTablePage/apiTablePageMessages';
import { appInitializerMessages } from 'components/AppInitializer/appInitializerMessages';
import { deviceViewMessages } from 'components/DeviceView/deviceViewMessages';
import { siteSeparationMessages } from 'components/SiteSeparation/siteSeparationMessages';
import { tableMessages } from 'components/Table/tableMessages';
import { tagsInputIpFieldMessages } from 'components/TagsInputIpField/tagsInputIpFieldMessages';
import jobManagement from 'containers/App/Header/JobManagement/messages';
import notifications from 'containers/App/Header/Notifications/messages';
import { requireSnapshotMessages } from 'containers/App/RequireSnapshot/messages';
import refreshDialog from 'containers/App/refreshDialogMessages';
import appLoader from 'containers/AppLoader/messages';

import copyToClipboard from 'components/CopyToClipboard/messages';
import { discoveryTasks } from 'components/DiscoveryTasks/discoveryTasksMessages';
import header from 'containers/App/Header/messages';
import downloadReport from 'containers/DownloadReport/messages';
import fileUploadOverlay from 'containers/FileUploadOverlay/messages';
import hostnameTableDetail from 'containers/HostnameDetail/messages';
import leaveDialogue from 'containers/LeaveDialogue/messages';
import scheduleField from 'containers/ScheduleField/messages';
import { authMessages } from 'pages/Auth/messages';
import { canvasGraphMessages } from 'pages/CanvasGraph/messages';
import dashboardOverview from 'pages/Dashboard/Overview/messages';
import { manualSeparationMessages } from 'pages/Inventory/Sites/ManualSeparation/messages';
import { licenseLimitExceededMessages } from 'pages/LicenseLimitExceeded/messages';
import { maintenanceMessages } from 'pages/Maintenance/messages';
import { managementChangesMessages } from 'pages/Management/Changes/messages';
import { managementConfigurationMessages } from 'pages/Management/Configuration/messages';
import { managementSavedConfigConsistencyMessages } from 'pages/Management/SavedConfigConsistency/messages';
import { notFoundMessages } from 'pages/NotFoundPage/messages';
import { permissionsRequiredMessages } from 'pages/PermissionsRequired/messages';
import { networkAnalysisReportMessages } from 'pages/Reports/NetworkAnalysisReport/messages';
import { siteLowLevelDesignMessages } from 'pages/Reports/SiteLowLevelDesign/messages';
import search from 'pages/Search/messages';
import { administrationPoliciesMessages } from 'pages/Settings/Administration/Policies/administrationPoliciesMessages';
import { administrationRolesMessages } from 'pages/Settings/Administration/Roles/administrationRolesMessages';
import { settingsDeviceAttributesMessages } from 'pages/Settings/DiscoverySnapshots/GlobalDiscoveryConfiguration/DeviceAttributes/settingsDeviceAttributesMessages';
import { apiTokens } from 'pages/Settings/Integration/ApiTokens/messages';
import { snapshotManagementErrorsMessages } from 'pages/SnapshotManagement/Errors/messages';
import { snapshotManagementTasksMessages } from 'pages/SnapshotManagement/Tasks/messages';
import { requestMessages } from 'utils/request/requestMessages';
import exceptions from './exceptions';

import { deviceCredentialsMessages } from 'components/DeviceCredentials/deviceCredentialsMessages';
import settingsDiscoverySeed from 'components/DiscoverySeed/messages';
import administrationLdapSettings from 'pages/Settings/Administration/LdapSettings/messages';
import { localUsersMessages } from 'pages/Settings/Administration/LocalUsers/localUsersMessages';
import { ouiMessages } from 'pages/Settings/DiscoverySnapshots/GlobalDiscoveryConfiguration/Oui/messages';
import { settingsGlobalDiscoveryConfigurationMessages } from 'pages/Settings/DiscoverySnapshots/GlobalDiscoveryConfiguration/messages';
import { settingsWebHooks } from 'pages/Settings/Integration/WebHooks/messages';

import { animationCloseMessages } from 'components/AnimationClose/animationCloseMessages';
import { formikFilterTextFieldMessages } from 'components/FormikFilterTextField/FormikFilterTextFieldMessages';
import { formikMultiValueFieldMessages } from 'components/FormikMultiValueField/formikMultiValueFieldMessages';
import { nothingToSeeHereMessages } from 'components/NothingToSeeHere/nothingToSeeHereMessages';
import { recoverableLoaderMessages } from 'components/RecoverableLoader/recoverableLoaderMessages';
import { instanceMembersMessages } from 'components/STPInstanceMembers/instanceMembersMessages';
import { securitySchemaMessages } from 'components/SecuritySchema/securitySchemaMessages';
import { sidebarMessages } from 'components/Sidebar/sidebarMessages';
import { snapshotCreationMessages } from 'components/SnapshotCreation/snapshotCreationMessages';
import { socketDisconnectedMessages } from 'components/SocketDisconnected/socketDisconnectedMessages';
import { advancedFilterMessages } from 'components/Table/components/AdvancedFilter/advancedFilterMessages';
import { discoverySettingsMessages } from 'features/DiscoverySettingsForms/translations/discoverySettingsMessages';
import { userProfileMessages } from 'pages/Me/UserProfile/userProfileMessages';
import { configurationManagementMessages } from 'pages/Settings/ConfigurationManagement/configurationManagementMessages';
import { snapshotCollectionMessages } from 'pages/Settings/DiscoverySnapshots/SnapshotCollection/snapshotCollectionMessages';
import settingsSystemCertificationAuthrorities from 'pages/Settings/System/BackupMaintenance/CertificationAuthorities/messages';
import { backupMaintenanceMessages } from 'pages/Settings/System/BackupMaintenance/backupMaintenanceMessages';
import { snapshotManagementMessages } from 'pages/SnapshotManagement/messages';
import supportDiagnostics from 'pages/Support/SendDiagnostics/messages';
import supportVpn from 'pages/Support/Vpn/messages';
import uploadLicense from 'pages/UploadLicense/messages';
import { genericMessages } from './generic';

/**
 * Localizations are deprecated. Here https://ipfabric.atlassian.net/wiki/spaces/DEV/pages/3773693976/2024-03-28+WebSink#No-need-to-use-locale-anymore
 * we decided to abandon them. Reasoning:
 * * API related stuff (errors, messages, OAS) is not using any locale abstraction either.
 * * There are no plans to implement actual localizations in foreseeable future.
 * * Maintenance cost is non-trivial, makes writing any text content one order more difficult
 *   and would need extra care in matters like moving components to shared UI package.
 * * This locale approach in source code needs to be maintained and updated by developers,
 *   is not flexible to pass to professional translators and would probably need significant change
 *   if we decide to actually localize the product.
 *
 * For now, hardcode any messages in the code instead.
 */
// inference is desired here

export function translations(lng: string) {
  const getMessages = <T extends { en: unknown }>(messages: T): T['en'] =>
    (messages as any)[lng] ?? messages.en;
  return {
    animationClose: getMessages(animationCloseMessages),
    apiTable: getMessages(apiTableMessages),
    apiTablePage: getMessages(apiTablePageMessages),
    app: {
      header: getMessages(header),
      sidebar: getMessages(sidebarMessages),
      requireSnapshot: getMessages(requireSnapshotMessages),
      refreshDialog: getMessages(refreshDialog),
    },
    appLoader: getMessages(appLoader),
    appInitializer: getMessages(appInitializerMessages),
    exceptions: getMessages(exceptions),
    deviceView: getMessages(deviceViewMessages),
    administrationLdapSettings: getMessages(administrationLdapSettings),
    administrationPolicies: getMessages(administrationPoliciesMessages),
    administrationRolesMessages: getMessages(administrationRolesMessages),
    auth: getMessages(authMessages),
    copyToClipboard: getMessages(copyToClipboard),
    downloadReport: getMessages(downloadReport),
    fileUploadOverlay: getMessages(fileUploadOverlay),
    canvasGraph: getMessages(canvasGraphMessages),
    licenseLimitExceeded: getMessages(licenseLimitExceededMessages),
    localUsers: getMessages(localUsersMessages),
    notFound: getMessages(notFoundMessages),
    permissionsRequired: getMessages(permissionsRequiredMessages),
    request: getMessages(requestMessages),
    scheduleField: getMessages(scheduleField),
    securitySchema: getMessages(securitySchemaMessages),
    deviceCredentials: getMessages(deviceCredentialsMessages),
    settings: {
      apiTokens: getMessages(apiTokens),
      globalDiscoveryConfiguration: getMessages(
        settingsGlobalDiscoveryConfigurationMessages,
      ),
      discoverySeed: getMessages(settingsDiscoverySeed),
      oui: getMessages(ouiMessages),
      webHooks: getMessages(settingsWebHooks),
      deviceAttributes: getMessages(settingsDeviceAttributesMessages),
    },
    siteSeparation: getMessages(siteSeparationMessages),
    support: {
      vpn: getMessages(supportVpn),
    },
    jobManagement: getMessages(jobManagement),
    notifications: getMessages(notifications),
    table: getMessages(tableMessages),
    settingsSystemCertificationAuthrorities: getMessages(
      settingsSystemCertificationAuthrorities,
    ),
    technologySpanTreeInstanceMembers: getMessages(instanceMembersMessages),
    manualSeparation: getMessages(manualSeparationMessages),
    networkAnalysisReport: getMessages(networkAnalysisReportMessages),
    siteLowLevelDesign: getMessages(siteLowLevelDesignMessages),
    dashboardOverview: getMessages(dashboardOverview),
    maintenance: getMessages(maintenanceMessages),
    managementConfiguration: getMessages(managementConfigurationMessages),
    managementSavedConfigConsistency: getMessages(
      managementSavedConfigConsistencyMessages,
    ),
    managementChanges: getMessages(managementChangesMessages),
    supportDiagnostics: getMessages(supportDiagnostics),
    uploadLicense: getMessages(uploadLicense),
    snapshotManagementErrors: getMessages(snapshotManagementErrorsMessages),
    snapshotManagementTasks: getMessages(snapshotManagementTasksMessages),
    search: getMessages(search),
    tagsInputIpField: getMessages(tagsInputIpFieldMessages),
    hostnameTableDetail: getMessages(hostnameTableDetail),
    leaveDialogue: getMessages(leaveDialogue),
    snapshotManagement: getMessages(snapshotManagementMessages),
    discoveryTasks: getMessages(discoveryTasks),
    formikFilterText: getMessages(formikFilterTextFieldMessages),
    formikMultiValue: getMessages(formikMultiValueFieldMessages),
    nothingToSeeHere: getMessages(nothingToSeeHereMessages),
    recoverableLoader: getMessages(recoverableLoaderMessages),
    socketDisconnected: getMessages(socketDisconnectedMessages),
    snapshotCreation: getMessages(snapshotCreationMessages),
    generic: getMessages(genericMessages),
    userProfile: getMessages(userProfileMessages),
    advancedFilter: getMessages(advancedFilterMessages),
    discoverySettings: getMessages(discoverySettingsMessages),
    systemBackupMaintenance: getMessages(backupMaintenanceMessages),
    settingsConfigurationManagement: getMessages(
      configurationManagementMessages,
    ),
    snapshotCollection: getMessages(snapshotCollectionMessages),
  };
}

export type AppTranslations = ReturnType<typeof translations>;
export type SettingsJumphostTranslations =
  AppTranslations['settings']['globalDiscoveryConfiguration']['jumphost'];
export type CustomPortSettingsTranslations =
  AppTranslations['discoverySettings']['customPortSettings'];
